import { useContext, useState } from 'react';
import { MappingContext } from '../../contexts/MappingContext';
import {
  getElement,
  getCellElement,
  removeBackgroundColor,
  updateAllSpanBackgroundColors,
  escapeHtml,
} from '../../helpers/generalizationHelpers';
import { EditorRefObject } from '../../types';
import { v4 } from 'uuid';
import { AUTHORING_COLORS } from '../../constants';
import { GeneralizationContext } from '../../contexts/GeneralizationContext';

export const useTextSelection = () => {
  const { editor1Ref, editor2Ref } = useContext(MappingContext);
  const { targetNodeIdSuggestionMap } = useContext(GeneralizationContext);

  const [targetSelection, setTargetSelection] = useState<HTMLElement | null>();
  const [sourceSelection, setSourceSelection] = useState<HTMLElement | null>();

  const templateSelectionOnClickHandler = (target: HTMLElement): string | null => {
    const editor1 = editor1Ref.current;
    if (!editor1 || editor1.selection.getSelectedBlocks().length > 1) return null;

    const spans = editor1.getBody().querySelectorAll('span[data-selectionid][data-sourceid]');
    updateAllSpanBackgroundColors(spans as NodeListOf<HTMLElement>);

    const selection = editor1.selection.getSel();
    const selectedText = selection ? selection.toString() : '';

    const nodeId =
      target.getAttribute('data-nodeid') ??
      selection?.anchorNode?.parentElement?.getAttribute('data-nodeid');

    nodeId &&
      updateDocumentSelection(
        selectedText,
        !!nodeId,
        setTargetSelection,
        targetSelection,
        editor1Ref,
        nodeId,
      );

    resetSourceTextHighlight();

    return selectedText ? (nodeId ?? null) : highlightSpan(target);
  };

  const highlightSpan = (element: HTMLElement): string | null => {
    if (element.nodeName === 'SPAN' && element.getAttribute('data-selectionid')) {
      const parent = element.parentNode;
      if (parent) {
        return (parent as HTMLElement).getAttribute('data-nodeid');
      }
    }
    const hasDataHighlight = Array.from(element.getElementsByTagName('*')).some((ele: Element) =>
      ele.hasAttribute('data-highlight'),
    );
    return hasDataHighlight ? element.getAttribute('data-nodeid') : null;
  };

  const sourceSelectionOnClickHandler = (target: HTMLElement): string | null => {
    if (!targetSelection || target.nodeName === 'DIV') return null;

    const editor2 = editor2Ref.current;
    if (!editor2) return null;

    const cellElement = getCellElement(target, editor2.getBody());
    const clickedNodeId = target.getAttribute('data-nodeid') || '';
    const selection = editor2.selection.getSel();

    const selectedText = selection ? selection.toString() : '';

    const nodeId = cellElement?.getAttribute('data-nodeid') ?? target.getAttribute('data-nodeid');

    updateDocumentSelection(
      selectedText,
      !!nodeId,
      setSourceSelection,
      sourceSelection,
      editor2Ref,
      clickedNodeId,
    );

    const resolvedNodeId =
      selectedText && (cellElement?.getAttribute('data-nodeid') ?? clickedNodeId);
    return resolvedNodeId || null;
  };

  const processSelection = (
    sourceSelection: HTMLElement | null | undefined,
    targetSelection: HTMLElement | null | undefined,
    editor2Ref: EditorRefObject,
    transferSelection = false,
  ) => {
    if (sourceSelection && targetSelection) {
      const sourceDocumentText = sourceSelection.querySelector('[data-highlight="active"]');
      const updatedTargetNode = targetSelection.querySelector('[data-highlight="active"]');
      const oldTargetNodeValue = targetSelection?.innerHTML;
      const targetNodeDocId = targetSelection?.getAttribute('data-docid');
      const newSpanDataNodeId = v4();
      const newTargetDataNodeId = v4();

      if (sourceDocumentText && updatedTargetNode) {
        sourceDocumentText.setAttribute('data-highlight', 'partial');
        updatedTargetNode.setAttribute('data-highlight', 'partial');
        const sourceNodeId = sourceSelection.getAttribute('data-nodeId');
        const sourceCellElement = getCellElement(
          sourceDocumentText,
          editor2Ref.current?.getBody() as Node,
        );
        let tdSourceId: string | null = null;
        if (sourceNodeId) {
          if (sourceCellElement) {
            tdSourceId = sourceCellElement.getAttribute('data-nodeId');
          }
          sourceDocumentText.setAttribute('data-sourceid', tdSourceId ?? sourceNodeId);
          updatedTargetNode.setAttribute('data-sourceid', tdSourceId ?? sourceNodeId);
          const selectionId = v4();

          targetSelection.setAttribute('data-nodeid', newTargetDataNodeId);
          targetSelection.setAttribute('data-newnode', 'true');

          sourceDocumentText.setAttribute('data-selectionid', selectionId);
          updatedTargetNode.setAttribute('data-nodeid', newSpanDataNodeId);
          updatedTargetNode.innerHTML = ` ${updatedTargetNode.innerHTML} `;
          const childNodes = Array.from(targetSelection.childNodes);

          const processNode = (node: Node, parentIndex: number) => {
            if (node.nodeType === Node.TEXT_NODE && node.textContent?.trim()) {
              const span = document.createElement('span');
              span.textContent = node.textContent;
              if (
                !(
                  span.getAttribute('data-nodeid') &&
                  targetNodeIdSuggestionMap[span.getAttribute('data-nodeid') as string]
                )
              ) {
                span.setAttribute('data-nodeid', v4());
              }
              span.setAttribute('data-docid', targetNodeDocId ?? '');
              span.setAttribute('data-nodeorder', (parentIndex + 1).toString());
              span.setAttribute('data-newnode', 'true');
              node.parentNode?.replaceChild(span, node);
            } else if (node.nodeType === Node.ELEMENT_NODE) {
              const element = node as HTMLElement;
              if (
                element.getAttribute('data-nodeid') !== newSpanDataNodeId &&
                !targetNodeIdSuggestionMap[element.getAttribute('data-nodeid') as string]
              ) {
                element.setAttribute('data-nodeid', v4());
              }
              element.setAttribute('data-docid', targetNodeDocId ?? '');
              element.setAttribute('data-nodeorder', (parentIndex + 1).toString());
              element.setAttribute('data-newnode', 'true');

              // Process child nodes recursively
              Array.from(element.childNodes).forEach((child, index) => {
                processNode(child, index);
              });
            }
          };

          childNodes.forEach((child, index) => {
            processNode(child, index);
          });
        }
        if (transferSelection) updatedTargetNode.innerHTML = sourceDocumentText.innerHTML;
      }
      setSourceSelection(null);
      setTargetSelection(null);

      return {
        sourceValue: removeBackgroundColor(sourceSelection.innerHTML),
        targetValue: removeBackgroundColor(updatedTargetNode?.innerHTML as string),
        oldTargetNodeValue: removeBackgroundColor(oldTargetNodeValue),
        newSpanDataNodeId,
      };
    }
  };

  const updateTextSelectionNode = () => {
    return processSelection(sourceSelection, targetSelection, editor2Ref, true);
  };

  const linkTextSelectionNode = () => {
    return processSelection(sourceSelection, targetSelection, editor2Ref);
  };

  const updateDocumentSelection = (
    selectedText: string,
    isTdElementWithDataNodeId: boolean,
    setSelection: React.Dispatch<React.SetStateAction<HTMLElement | null | undefined>>,
    selection: HTMLElement | null | undefined,
    editorRef: EditorRefObject,
    clickedNodeId: string,
  ) => {
    if (!selectedText || !isTdElementWithDataNodeId) {
      return;
    }
    if (selection) {
      selection
        .querySelector("span[data-highlight='active']")
        ?.setAttribute('data-highlight', 'remove');
    }

    const finalSelectedText = escapeHtml(selectedText);
    const spanElement = `<span style="background-color: ${AUTHORING_COLORS.NARRATIVE_SELECTION}" data-highlight="active">${finalSelectedText}</span>`;

    const editor = editorRef.current;
    editor?.execCommand('mceInsertContent', false, spanElement);
    const container = getElement(editorRef, clickedNodeId);
    setSelection(container);
    const activeSpan = container?.querySelector('span[data-highlight="active"]');
    if (editor && activeSpan) {
      editor.selection.select(activeSpan);
    }
    if (container) {
      const body = editor?.getBody();
      if (body) {
        const cellHost = (getCellElement(container, body) ?? container) as HTMLElement;
        cellHost.style.removeProperty('background-color');
        cellHost.removeAttribute('data-highlighted');
      }
    }
    if (selection) {
      removeHighlightedSpan(selection);
    }
  };

  const removeHighlightedSpan = (element: HTMLElement) => {
    const spanToRemove = element.querySelector("span[data-highlight='remove']");
    if (spanToRemove) {
      const parentElement =
        spanToRemove.parentElement === element ? element : spanToRemove.parentElement;
      if (parentElement) {
        while (spanToRemove.firstChild) {
          parentElement.insertBefore(spanToRemove.firstChild, spanToRemove);
        }
        spanToRemove.remove();
      }
    }
  };

  const resetActiveTextHighlight = (selection: HTMLElement | null | undefined) => {
    if (selection) {
      const activeSpan = selection.querySelector(
        "span[data-highlight='active']",
      ) as HTMLElement | null;
      if (activeSpan) {
        activeSpan.setAttribute('data-highlight', 'remove');
        removeHighlightedSpan(selection);
      }
    }
  };

  const resetSourceTextHighlight = () => {
    if (!sourceSelection) return;

    resetActiveTextHighlight(sourceSelection);
    setSourceSelection(null);
  };

  const resetAllActiveTextHighlights = () => {
    resetActiveTextHighlight(targetSelection);
    resetSourceTextHighlight();
    setTargetSelection(null);
  };

  return {
    templateSelectionOnClickHandler,
    sourceSelectionOnClickHandler,
    updateTextSelectionNode,
    linkTextSelectionNode,
    resetAllActiveTextHighlights,
    resetSourceTextHighlight,
    sourceSelection,
    targetSelection,
  };
};
